import { createContext, useContext, useEffect, type HTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type DialogContextValue = {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
};

const DialogContext = createContext<DialogContextValue | null>(null);

export function Dialog({
  open,
  onOpenChange,
  children,
}: {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  return <DialogContext.Provider value={{ open, onOpenChange }}>{children}</DialogContext.Provider>;
}

export function DialogTrigger({
  children,
  ...props
}: HTMLAttributes<HTMLButtonElement> & { children: ReactNode }) {
  const ctx = useContext(DialogContext);
  return (
    <button
      type="button"
      {...props}
      onClick={(event) => {
        props.onClick?.(event);
        ctx?.onOpenChange?.(true);
      }}
    >
      {children}
    </button>
  );
}

export function DialogClose({
  children,
  ...props
}: HTMLAttributes<HTMLButtonElement> & { children?: ReactNode }) {
  const ctx = useContext(DialogContext);
  return (
    <button
      type="button"
      {...props}
      onClick={(event) => {
        props.onClick?.(event);
        ctx?.onOpenChange?.(false);
      }}
    >
      {children ?? <XIcon />}
    </button>
  );
}

export function DialogPortal({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export function DialogOverlay({ className = '', ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`fixed inset-0 z-[1000] bg-black/50 ${className}`} {...props} />;
}

export function DialogContent({
  className = '',
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & { children: ReactNode }) {
  const ctx = useContext(DialogContext);
  const open = ctx?.open ?? false;

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        ctx?.onOpenChange?.(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [ctx, open]);

  if (!open) return null;

  return createPortal(
    <DialogPortal>
      <DialogOverlay
        onClick={() => ctx?.onOpenChange?.(false)}
        aria-hidden="true"
      />
      <div
        className={`fixed left-1/2 top-1/2 z-[1000] w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-[20px] border border-[#EFEFEF] bg-white p-6 shadow-lg ${className}`}
        role="dialog"
        aria-modal="true"
        {...props}
      >
        {children}
        <DialogClose className="absolute right-4 top-4 opacity-70 hover:opacity-100" />
      </div>
    </DialogPortal>,
    document.body,
  );
}

export function DialogHeader({ className = '', ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`flex flex-col gap-2 text-center sm:text-left ${className}`} {...props} />;
}

export function DialogFooter({ className = '', ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`flex flex-col-reverse gap-2 sm:flex-row sm:justify-end ${className}`} {...props} />;
}

export function DialogTitle({ className = '', ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={`text-lg font-semibold ${className}`} {...props} />;
}

export function DialogDescription({ className = '', ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={`text-sm text-grey1 ${className}`} {...props} />;
}

const XIcon = ({ className = '', ...props }: HTMLAttributes<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" className={`size-4 ${className}`} {...props} aria-hidden="true">
    <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
